// ###### IMPORTS
import { apiClient } from "connections/apiClient";
import apiRoutes from "connections/apiRoutes";

// ============================================
// PROGRAMS - API CALLS
// ============================================

/**
 * Get list of all programs for the current implementation
 * @param {string} status - Optional filter by status (active, inactive, archived)
 * @returns {Promise<object>} List of programs with scenario counts
 */
export const getPrograms = async (status = null) => {
  try {
    const params = status ? { status } : {};
    const response = await apiClient.get(apiRoutes.programs.list, { params });
    return response.data;
  } catch (error) {
    console.error("Failed to get programs:", error);
    throw error;
  }
};

/**
 * Get detailed information about a specific program
 * @param {number} programId - The program ID
 * @returns {Promise<object>} Program details with scenarios grouped by type
 */
export const getProgramDetail = async (programId) => {
  try {
    const response = await apiClient.get(
      `${apiRoutes.programs.detail}${programId}/`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to get program detail:", error);
    throw error;
  }
};

/**
 * Create a new program
 * @param {object} data - Program data { program_name, program_description, program_status }
 * @returns {Promise<object>} Created program info
 */
export const createProgram = async (data) => {
  try {
    const response = await apiClient.post(apiRoutes.programs.create, data);
    return response.data;
  } catch (error) {
    console.error("Failed to create program:", error);
    throw error;
  }
};

/**
 * Update a program
 * @param {number} programId - The program ID
 * @param {object} data - Fields to update { program_name, program_description, program_status }
 * @returns {Promise<object>} Updated program info
 */
export const updateProgram = async (programId, data) => {
  try {
    const response = await apiClient.patch(
      `${apiRoutes.programs.update}${programId}/update/`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update program:", error);
    throw error;
  }
};

/**
 * Delete a program
 * @param {number} programId - The program ID
 * @returns {Promise<object>} Deletion confirmation
 */
export const deleteProgram = async (programId) => {
  try {
    const response = await apiClient.delete(
      `${apiRoutes.programs.delete}${programId}/delete/`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to delete program:", error);
    throw error;
  }
};

/**
 * Get available scenarios for a program (not yet assigned)
 * @param {number} programId - The program ID
 * @returns {Promise<object>} List of available scenarios
 */
export const getAvailableScenarios = async (programId) => {
  try {
    const response = await apiClient.get(
      `${apiRoutes.programs.availableScenarios}${programId}/available-scenarios/`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to get available scenarios:", error);
    throw error;
  }
};

/**
 * Add a scenario to a program
 * @param {number} programId - The program ID
 * @param {object} data - { scenario_id, scenario_type, display_order }
 * @returns {Promise<object>} Added scenario info
 */
export const addScenarioToProgram = async (programId, data) => {
  try {
    const response = await apiClient.post(
      `${apiRoutes.programs.addScenario}${programId}/scenarios/`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to add scenario to program:", error);
    throw error;
  }
};

/**
 * Remove a scenario from a program
 * @param {number} programId - The program ID
 * @param {number} programScenarioId - The program scenario association ID
 * @returns {Promise<object>} Removal confirmation
 */
export const removeScenarioFromProgram = async (
  programId,
  programScenarioId,
) => {
  try {
    const response = await apiClient.delete(
      `${apiRoutes.programs.removeScenario}${programId}/scenarios/${programScenarioId}/remove/`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to remove scenario from program:", error);
    throw error;
  }
};

// ============================================
// COMPANY - API CALLS
// ============================================

/**
 * Get Financial Performance data (P&L) for Company Card 1
 * @param {number} programId - The program ID
 * @param {object} filters - Optional filters (products, brands, regions, clients, pnl_flag)
 * @returns {Promise<object>} P&L data by scenario type (actual/forecast/policy)
 */
export const getFinancialPerformanceData = async (programId, filters = {}) => {
  try {
    const payload = {
      program_id: programId,
      ...filters,
    };

    const response = await apiClient.post(
      apiRoutes.executive.financialPerformance,
      payload,
    );
    console.log("getFinancialPerformanceData - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get Financial Performance data:", error);
    throw error;
  }
};

/**
 * Get Investment Management data (P&L) for Company Card 2
 * @param {number} programId - The program ID
 * @param {object} filters - Optional filters (products, brands, regions, clients, pnl_flag)
 * @returns {Promise<object>} P&L data by scenario type (actual/forecast/policy)
 */
export const getInvestmentManagementData = async (programId, filters = {}) => {
  try {
    const payload = {
      program_id: programId,
      ...filters,
    };

    const response = await apiClient.post(
      apiRoutes.executive.investmentManagement,
      payload,
    );
    console.log("getInvestmentManagementData - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get Investment Management data:", error);
    throw error;
  }
};

// ============================================
// CHANNELS - API CALLS
// ============================================

/**
 * Get Channel Transparency data for Channels Card 1
 * @param {number} programId - The program ID
 * @param {object} filters - Optional filters (products, brands, regions, clients)
 * @returns {Promise<object>} Channel data by scenario type (actual/forecast/policy)
 */
export const getChannelTransparencyData = async (programId, filters = {}) => {
  try {
    const payload = {
      program_id: programId,
      ...filters,
    };

    const response = await apiClient.post(
      apiRoutes.executive.channelTransparency,
      payload,
    );
    console.log("getChannelTransparencyData - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get Channel Transparency data:", error);
    throw error;
  }
};

/**
 * Get Chain Monitoring data for Channels Card 2
 * @param {number} programId - The program ID
 * @param {object} filters - Optional filters (products, brands, regions, clients, solver_status)
 * @param {string} weightField - Weight field for calculations (default: 'input_sellthrough_volume_sales')
 * @param {string} consideredRoutes - Routes to consider (default: '')
 * @returns {Promise<object>} Chain data by scenario type (actual/forecast/policy)
 */
export const getChainMonitoringData = async (
  programId,
  filters = {},
  weightField = "input_sellthrough_volume_sales",
  consideredRoutes = "",
) => {
  try {
    const payload = {
      program_id: programId,
      weight_field: weightField,
      considered_routes: consideredRoutes,
      ...filters,
    };

    const response = await apiClient.post(
      apiRoutes.executive.chainMonitoring,
      payload,
    );
    console.log("getChainMonitoringData - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get Chain Monitoring data:", error);
    throw error;
  }
};

// ============================================
// MARKET - API CALLS
// ============================================

/**
 * Get Competition Positioning data (P&L) for Market Card 1
 * @param {number} programId - The program ID
 * @param {object} filters - Optional filters (products, brands, regions, clients, pnl_flag)
 * @returns {Promise<object>} P&L data by scenario type (actual/forecast/policy)
 */
export const getCompetitionPositioningData = async (
  programId,
  filters = {},
) => {
  try {
    const payload = {
      program_id: programId,
      ...filters,
    };

    const response = await apiClient.post(
      apiRoutes.executive.competitionPositioning,
      payload,
    );
    console.log("getCompetitionPositioningData - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get Competition Positioning data:", error);
    throw error;
  }
};

/**
 * Get Volume Market Share data (P&L) for Market Card 2
 * @param {number} programId - The program ID
 * @param {object} filters - Optional filters (products, brands, regions, clients, pnl_flag)
 * @returns {Promise<object>} P&L data by scenario type (actual/forecast/policy)
 */
export const getVolumeMarketShareData = async (programId, filters = {}) => {
  try {
    const payload = {
      program_id: programId,
      ...filters,
    };

    const response = await apiClient.post(
      apiRoutes.executive.volumeMarketShare,
      payload,
    );
    console.log("getVolumeMarketShareData - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get Volume Market Share data:", error);
    throw error;
  }
};
